import { ArrowLeft, ArrowRight, Check, Receipt } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { money } from "@/lib/format";
import { studioSummary } from "@/components/StudioInspector";

// Pricing confirmation shown when the 3D step is finished: what will be synced into the quote lines.
export const SendToQuoteDialog = ({ open, onClose, onConfirm, quote, totals, saving }) => {
  const areas = quote?.areas || [];
  const sums = areas.map((a) => studioSummary(a, totals));
  const pieces = sums.reduce((n, s) => n + s.pieces.length, 0);
  const sqft = sums.reduce((n, s) => n + s.sqft, 0);
  const edgeLf = sums.reduce((n, s) => n + s.edgeFt.reduce((a, [, f]) => a + f, 0), 0);
  const cutouts = sums.reduce((n, s) => n + s.cutouts.reduce((a, [, c]) => a + c, 0), 0);
  const slabs = (quote?.layout?.slabs || []).length;
  const Row = ({ k, v, testId }) => (
    <div className="flex items-center justify-between border-b border-zinc-800/60 py-2 text-xs">
      <span className="text-zinc-400">{k}</span>
      <span className="font-mono-data font-semibold tabular-nums text-zinc-100" data-testid={testId}>{v}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md border-zinc-800 bg-zinc-900 text-zinc-100" data-testid="send-to-quote-dialog">
        <DialogTitle className="text-base font-bold">Send design to quote</DialogTitle>
        <DialogDescription className="text-xs text-zinc-400">These figures are synced into the quote line items and priced on save.</DialogDescription>
        <div className="mt-2">
          <Row k="Pieces" v={`${pieces} · ${sqft.toFixed(2)} ft²`} testId="stq-pieces" />
          <Row k="Slabs in layout" v={slabs || "—"} testId="stq-slabs" />
          <Row k="Finished edge" v={`${edgeLf.toFixed(1)} lf`} testId="stq-edges" />
          <Row k="Cut-outs" v={cutouts} testId="stq-cutouts" />
        </div>
        <div className="mt-3 flex items-end justify-between rounded-xl border border-amber-400/25 bg-amber-500/5 p-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Quote total</span>
          <span className="font-mono-data text-2xl font-bold tabular-nums text-zinc-50" data-testid="stq-total">{money(totals?.total || 0)}</span>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={() => onClose?.()} data-testid="stq-cancel" className="studio-btn-ghost">Keep designing</button>
          <button type="button" disabled={saving} onClick={onConfirm} data-testid="stq-confirm" className="studio-btn-gold">
            <Receipt size={13} weight="bold" /> {saving ? "Saving…" : "Confirm & open quote"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Numbered step-flow bar: Draw → Slab layout → Vein match → 3D → Review.
export const StudioStepBar = ({ steps, current, onPick, onReview }) => {
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <nav className="studio-steps" data-testid="studio-tabs" aria-label="Studio steps">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const on = s.key === current;
        const done = i < idx;
        return (
          <button
            key={s.key}
            type="button"
            data-testid={`studio-tab-${s.key}`}
            title={s.hint}
            aria-current={on ? "step" : undefined}
            onClick={() => onPick(s.key)}
            className={`studio-step ${on ? "is-on" : done ? "is-done" : ""}`}
          >
            <span className="studio-step-num">
              {done ? <Check size={11} weight="bold" /> : i + 1}
            </span>
            <Icon size={14} weight="bold" className="studio-step-icon" />
            <span className="studio-step-label">{s.label}</span>
            {i < steps.length - 1 && <span className="studio-step-line" aria-hidden="true" />}
          </button>
        );
      })}
      <button
        type="button"
        data-testid="studio-tab-review"
        title="Save and open the quote review"
        onClick={onReview}
        className="studio-step studio-step-review"
      >
        <span className="studio-step-num">{steps.length + 1}</span>
        <Receipt size={14} weight="bold" className="studio-step-icon" />
        <span className="studio-step-label">Review</span>
      </button>
    </nav>
  );
};

// Sticky bottom bar on the Layout / Vein / 3D steps: back, next, and the gold "send to quote" CTA.
export const StudioStepFooter = ({ steps, current, onPick, onReview, total, saving }) => {
  const idx = steps.findIndex((s) => s.key === current);
  const prev = steps[idx - 1];
  const next = steps[idx + 1];
  return (
    <div className="studio-footer" data-testid="studio-step-footer">
      {prev ? (
        <button
          type="button"
          data-testid="studio-step-back"
          onClick={() => onPick(prev.key)}
          className="studio-btn-ghost"
        >
          <ArrowLeft size={13} weight="bold" /> {prev.label}
        </button>
      ) : (
        <span />
      )}
      <span className="studio-footer-total" data-testid="studio-footer-total">
        {money(total || 0)}
      </span>
      {next ? (
        <span />
      ) : (
        <button
          type="button"
          data-testid="studio-footer-review"
          disabled={saving}
          onClick={onReview}
          className="studio-btn-gold"
        >
          <Receipt size={13} weight="bold" /> {saving ? "Saving…" : "Review & send to quote"}
        </button>
      )}
    </div>
  );
};
